import { useRef, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, X, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Modal for uploading a CSV/XLSX import file.
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - endpoint (string) e.g. "/products/import" or "/movements/import"
 *  - templateEndpoint (string, optional) e.g. "/import/template/products"
 *  - title (string)
 *  - onSuccess() called after successful import to refresh parent
 *  - resultKey (string) - "created"/"updated"/"movements_created" to display; auto-picked
 */
export default function ImportModal({ open, onClose, endpoint, templateEndpoint, title, onSuccess, testidPrefix = "import" }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  if (!open) return null;

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(csv|xlsx)$/i.test(f.name)) {
      toast.error("Formato non supportato. Usa .csv o .xlsx");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Import completato");
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    if (!templateEndpoint) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}${templateEndpoint}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore download template"); }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-neutral-200 shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto" data-testid={`${testidPrefix}-modal`}>
        <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="label-eyebrow mb-1">Import Massivo</div>
            <h2 className="font-display font-bold text-xl tracking-tight">{title}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 transition-colors"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          {templateEndpoint && (
            <button
              type="button"
              data-testid={`${testidPrefix}-template`}
              onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-neutral-300 hover:border-[#002FA7] hover:bg-neutral-50 transition-colors text-sm text-neutral-700"
            >
              <DownloadSimple size={16} /> Scarica template XLSX
            </button>
          )}

          <label
            className="block cursor-pointer"
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) { const f = e.dataTransfer.files[0]; if (/\.(csv|xlsx)$/i.test(f.name)) setFile(f); } }}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={handleFile}
              className="hidden"
              data-testid={`${testidPrefix}-file`}
            />
            <div className={`border-2 border-dashed p-8 text-center transition-colors ${file ? "border-[#002FA7] bg-[#002FA7]/5" : "border-neutral-300 hover:border-neutral-400"}`}>
              <UploadSimple size={32} className="mx-auto mb-3 text-neutral-400" />
              {file ? (
                <div className="text-sm">
                  <div className="font-medium text-neutral-900">{file.name}</div>
                  <div className="text-xs text-neutral-500 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
              ) : (
                <>
                  <div className="text-sm font-medium text-neutral-700">Clicca o trascina un file .csv o .xlsx</div>
                  <div className="text-xs text-neutral-500 mt-1">Max 10MB</div>
                </>
              )}
            </div>
          </label>

          {result && (
            <div className="border border-neutral-200 divide-y divide-neutral-100">
              <div className="p-3 bg-emerald-50 flex items-center gap-2">
                <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                <div className="text-sm font-medium text-emerald-800" data-testid={`${testidPrefix}-result`}>
                  {result.created !== undefined && <>Creati: <b>{result.created}</b> · Aggiornati: <b>{result.updated}</b></>}
                  {result.movements_created !== undefined && <>Movimenti creati: <b>{result.movements_created}</b> · Seriali importati: <b>{result.serials_imported}</b></>}
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="p-3 bg-amber-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Warning size={14} weight="bold" className="text-amber-700" />
                    <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">{result.errors.length} avvisi</span>
                  </div>
                  <ul className="text-xs text-amber-800 space-y-0.5 max-h-32 overflow-y-auto font-mono">
                    {result.errors.slice(0, 20).map((e) => <li key={e}>· {e}</li>)}
                    {result.errors.length > 20 && <li className="text-amber-600">... e altri {result.errors.length - 20}</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 pt-0 flex justify-between gap-2 border-t border-neutral-200 mt-4">
          <button type="button" onClick={reset} disabled={!file} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors disabled:opacity-40">Reset</button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Chiudi</button>
            <button
              type="button"
              data-testid={`${testidPrefix}-submit`}
              onClick={upload}
              disabled={!file || uploading}
              className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors disabled:opacity-40"
            >
              {uploading ? "Import in corso..." : "Importa"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
